import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  Search,
  UserPlus,
  UserMinus,
  UserCheck,
  X,
  Mail,
  MessageSquare,
  Share2,
  Copy,
  Check,
  Clock,
  Flag,
  Plus,
  Globe,
  Lock,
  ChevronRight,
  Crown,
  LogOut,
  Trash2
} from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Input } from "./ui/input";
import UserProfileModal from "./UserProfileModal";
import UserBubble from "./UserBubble";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Helper function to get glow class based on engagement status
const getEngagementGlowClass = (member) => {
  if (member?.engagement_status === 'chatting' || member?.is_chatting) {
    return 'chatting-glow';
  } else if (member?.engagement_status === 'online' || member?.is_online) {
    return 'online-glow';
  }
  return '';
};

// Golf Buddies & Communities Page
export const FriendsPage = ({ user }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('buddies');
  const [friends, setFriends] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [allMembers, setAllMembers] = useState([]); // All members for Find tab
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Golf buddy requests
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  
  // Profile modal
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  // Buddy request review modal (shown when clicking notification)
  const [reviewingRequest, setReviewingRequest] = useState(null);
  
  // Community state
  const [communities, setCommunities] = useState([]);
  const [ownedCommunities, setOwnedCommunities] = useState([]);
  const [joinedCommunities, setJoinedCommunities] = useState([]);
  const [showCreateCommunity, setShowCreateCommunity] = useState(false);
  const [showJoinCommunity, setShowJoinCommunity] = useState(false);
  const [communitySearch, setCommunitySearch] = useState('');
  const [communitySearchResults, setCommunitySearchResults] = useState([]);
  const [newCommunity, setNewCommunity] = useState({ name: '', description: '', privacy: 'public', tags: '' });
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [communityRequests, setCommunityRequests] = useState([]);

  // Fetch golf buddies (people user follows mutually)
  const fetchFriends = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`${API}/members`);
      const membersData = response.data.members || [];
      
      // Golf buddies are people the user is following
      setFriends(membersData.filter(m => m.is_following && !m.is_self));
      
      // Suggestions are people user is NOT following (limited to 10 for suggestions)
      setSuggestions(membersData.filter(m => !m.is_following && !m.is_self).slice(0, 10));
      
      // All members (excluding self) for the Find Members tab
      setAllMembers(membersData.filter(m => !m.is_self));
    } catch (error) {
      console.error("Error fetching golf buddies:", error);
      if (!silent) toast.error("Failed to load golf buddies");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Fetch golf buddy requests
  const fetchRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/golf-buddies/requests`);
      setIncomingRequests(response.data.incoming || []);
      setOutgoingRequests(response.data.outgoing || []);
    } catch (error) {
      console.error("Error fetching requests:", error);
    }
  }, []);

  // Fetch communities
  const fetchCommunities = useCallback(async () => {
    try {
      const [ownedRes, joinedRes, allRes] = await Promise.all([
        axios.get(`${API}/communities?filter=owned`),
        axios.get(`${API}/communities?filter=joined`),
        axios.get(`${API}/communities?filter=all`)
      ]);
      setOwnedCommunities(ownedRes.data.communities || []);
      setJoinedCommunities(joinedRes.data.communities || []);
      setCommunities(allRes.data.communities || []);
    } catch (error) {
      console.error("Error fetching communities:", error);
    }
  }, []);

  // Fetch community join requests (for owners)
  const fetchCommunityRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/communities/requests/pending`);
      setCommunityRequests(response.data.requests || []);
    } catch (error) {
      console.error("Error fetching community requests:", error);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchFriends();
    fetchRequests();
    fetchCommunities();
    fetchCommunityRequests();
    
    // Poll for updates
    const friendsInterval = setInterval(() => fetchFriends(true), 20000);
    const requestsInterval = setInterval(fetchRequests, 15000);
    const communitiesInterval = setInterval(fetchCommunities, 30000);
    const commRequestsInterval = setInterval(fetchCommunityRequests, 20000);
    
    return () => {
      clearInterval(friendsInterval);
      clearInterval(requestsInterval);
      clearInterval(communitiesInterval);
      clearInterval(commRequestsInterval);
    };
  }, [fetchFriends, fetchRequests, fetchCommunities, fetchCommunityRequests]);

  // Handle URL parameters (from notification deep links)
  useEffect(() => {
    const tab = searchParams.get('tab');
    const requestId = searchParams.get('request_id');
    
    // Set the correct tab from URL
    if (tab) {
      setActiveTab(tab);
    }
    
    // If a specific request_id is provided, find and show that request
    if (requestId && incomingRequests.length > 0) {
      const targetRequest = incomingRequests.find(r => r.request_id === requestId);
      if (targetRequest) {
        setReviewingRequest(targetRequest);
        // Clear the URL params after showing the modal
        setSearchParams({});
      }
    }
  }, [searchParams, setSearchParams, incomingRequests]);

  // Search for golfers
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    
    try {
      setIsSearching(true);
      const response = await axios.get(`${API}/members`, {
        params: { search: searchQuery }
      });
      setSearchResults(response.data.members?.filter(m => !m.is_self) || []);
    } catch (error) {
      console.error("Error searching:", error);
    }
  }, [searchQuery]);

  useEffect(() => {
    const debounceTimer = setTimeout(handleSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [handleSearch]);

  const handleFollow = async (targetUserId) => {
    try {
      // Send a Golf Buddy request instead of instant follow
      await axios.post(`${API}/golf-buddies/request`, { target_user_id: targetUserId });
      toast.success("Golf Buddy request sent!");
      fetchFriends(true);
      fetchRequests();
      if (isSearching) {
        setSearchResults(prev => prev.map(m => 
          m.user_id === targetUserId ? { ...m, pending_request: 'outgoing' } : m
        ));
      }
    } catch (error) {
      if (error.response?.data?.detail?.includes('already')) {
        toast.info("Request already pending");
      } else {
        toast.error("Failed to send request");
      }
    }
  };

  const handleUnfollow = async (targetUserId) => {
    try {
      await axios.delete(`${API}/users/${targetUserId}/follow`);
      toast.success("Removed from Golf Buddies");
      fetchFriends(true);
      if (isSearching) {
        setSearchResults(prev => prev.map(m => 
          m.user_id === targetUserId ? { ...m, is_following: false } : m
        ));
      }
    } catch (error) {
      toast.error("Failed to remove buddy");
    }
  };

  // Community handlers
  const handleCreateCommunity = async () => {
    if (!newCommunity.name.trim()) {
      toast.error("Community name is required");
      return;
    }
    try {
      const tags = newCommunity.tags.split(',').map(t => t.trim().toLowerCase()).filter(t => t);
      const response = await axios.post(`${API}/communities/create`, {
        name: newCommunity.name,
        description: newCommunity.description,
        privacy: newCommunity.privacy,
        tags
      });
      toast.success(`${newCommunity.name} created!`);
      setShowCreateCommunity(false);
      setNewCommunity({ name: '', description: '', privacy: 'public', tags: '' });
      fetchCommunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create community");
    }
  };

  const handleJoinCommunity = async (communityId) => {
    try {
      const response = await axios.post(`${API}/communities/${communityId}/join`);
      toast.success(response.data.message);
      fetchCommunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join community");
    }
  };

  const handleLeaveCommunity = async (communityId) => {
    try {
      await axios.post(`${API}/communities/${communityId}/leave`);
      toast.success("Left community");
      fetchCommunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to leave community");
    }
  };

  const handleSearchCommunities = async () => {
    if (!communitySearch.trim()) {
      setCommunitySearchResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/communities/search?q=${communitySearch}`);
      setCommunitySearchResults(response.data.communities || []);
    } catch (error) {
      console.error("Error searching communities:", error);
    }
  };

  const handleAcceptCommunityRequest = async (requestId) => {
    try {
      await axios.post(`${API}/communities/requests/${requestId}/accept`);
      toast.success("Request accepted!");
      fetchCommunityRequests();
      fetchCommunities();
    } catch (error) {
      toast.error("Failed to accept request");
    }
  };

  const handleDeclineCommunityRequest = async (requestId) => {
    try {
      await axios.post(`${API}/communities/requests/${requestId}/decline`);
      toast.info("Request declined");
      fetchCommunityRequests();
    } catch (error) {
      toast.error("Failed to decline request");
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
  };

  // Invite link
  const inviteLink = `https://parseeker.com/join?ref=${user?.user_id || 'golf'}`;
  const inviteMessage = `Join me on Par Seeker - the golf social app! Track scores, find playing partners, and join games. ${inviteLink}`;

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent("Join me on Par Seeker!");
    const body = encodeURIComponent(inviteMessage);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareViaSMS = () => {
    const body = encodeURIComponent(inviteMessage);
    // Try iOS format first, fallback to Android
    const smsUrl = /iPhone|iPad|iPod/i.test(navigator.userAgent)
      ? `sms:&body=${body}`
      : `sms:?body=${body}`;
    window.location.href = smsUrl;
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Par Seeker',
          text: 'Join me on Par Seeker - track scores, find playing partners, and join games!',
          url: inviteLink
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyInviteLink();
        }
      }
    } else {
      copyInviteLink();
    }
  };

  // Member card component with profile modal trigger
  const MemberCard = ({ member, showRemove = false }) => {
    // Check if there's a pending request to this user
    const hasPendingOutgoing = outgoingRequests.some(r => r.to_user_id === member.user_id);
    const hasPendingIncoming = incomingRequests.some(r => r.from_user_id === member.user_id);
    
    return (
      <div 
        className="flex items-center justify-between p-4 bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)]"
        data-testid={`buddy-card-${member.user_id}`}
      >
        <div className="flex items-center gap-3 text-left">
          <UserBubble
            userId={member.user_id}
            picture={member.picture}
            name={member.name}
            bubbleColor={member.bubble_color || 'green'}
            isOnline={member.is_online}
            size="lg"
            onClick={() => setSelectedUserId(member.user_id)}
          />
          <div>
            <span 
              className={`font-semibold text-[var(--ps-text-primary)] block ${getEngagementGlowClass(member)}`}
            >
              {member.name}
            </span>
            {member.handicap !== null && (
              <span className="text-xs text-[var(--ps-text-secondary)]">
                Handicap: {member.handicap}
              </span>
            )}
          </div>
        </div>

        {showRemove ? (
          <button
            onClick={() => handleUnfollow(member.user_id)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
            data-testid={`remove-buddy-${member.user_id}`}
          >
            <UserMinus className="w-4 h-4" />
            Remove
          </button>
        ) : member.is_following || member.is_golf_buddy ? (
          <div className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-[var(--ps-primary)]/20 text-[var(--ps-primary)]">
            <UserCheck className="w-4 h-4" />
            Buddy
          </div>
        ) : hasPendingOutgoing ? (
          <div className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-500/10 text-yellow-500">
            <Clock className="w-4 h-4" />
            Pending
          </div>
        ) : hasPendingIncoming ? (
          <button
            onClick={() => {
              const request = incomingRequests.find(r => r.from_user_id === member.user_id);
              if (request) handleAcceptRequest(request.request_id);
            }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-[var(--ps-primary)] text-white hover:opacity-90 transition-opacity"
            data-testid={`accept-buddy-${member.user_id}`}
          >
            <UserCheck className="w-4 h-4" />
            Accept
          </button>
        ) : (
          <button
            onClick={() => handleFollow(member.user_id)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-[var(--ps-primary)] text-white hover:opacity-90 transition-opacity"
            data-testid={`request-buddy-${member.user_id}`}
          >
            <UserPlus className="w-4 h-4" />
            Request
          </button>
        )}
      </div>
    );
  };

  // Request card component
  const RequestCard = ({ request, type }) => (
    <div 
      className="flex items-center justify-between p-4 bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)]"
      data-testid={`request-card-${request.request_id}`}
    >
      <div className="flex items-center gap-3 text-left">
        <UserBubble
          userId={type === 'incoming' ? request.from_user_id : request.to_user_id}
          picture={type === 'incoming' ? request.from_user_picture : null}
          name={type === 'incoming' ? request.from_user_name : request.to_user_name}
          bubbleColor={request.bubble_color || 'green'}
          isOnline={request.is_online}
          size="lg"
          onClick={() => setSelectedUserId(type === 'incoming' ? request.from_user_id : request.to_user_id)}
        />
        <div>
          <span 
            className={`font-semibold text-[var(--ps-text-primary)] block ${getEngagementGlowClass(request)}`}
          >
            {type === 'incoming' ? request.from_user_name : request.to_user_name}
          </span>
          <span className="text-xs text-[var(--ps-text-muted)]">
            {type === 'incoming' ? 'Wants to be your golf buddy' : 'Request pending'}
          </span>
        </div>
      </div>

      {type === 'incoming' ? (
        <div className="flex gap-2">
          <button
            onClick={() => handleAcceptRequest(request.request_id)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-[var(--ps-primary)] text-white hover:opacity-90"
            data-testid={`accept-request-${request.request_id}`}
          >
            <UserPlus className="w-4 h-4" />
            Accept
          </button>
          <button
            onClick={() => handleDeclineRequest(request.request_id)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-red-500/10 text-red-500 hover:bg-red-500/20"
            data-testid={`decline-request-${request.request_id}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-amber-500/10 text-amber-500">
          <Clock className="w-4 h-4" />
          Pending
        </div>
      )}
    </div>
  );

  // Community card component
  const CommunityCard = ({ community, isOwner = false, isMember = false }) => (
    <div 
      className="p-4 bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)]"
      data-testid={`community-card-${community.community_id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-[var(--ps-primary)]/20 flex items-center justify-center">
            {community.privacy === 'private' ? (
              <Lock className="w-5 h-5 text-[var(--ps-primary)]" />
            ) : (
              <Globe className="w-5 h-5 text-[var(--ps-primary)]" />
            )}
          </div>
          <div>
            <span className="font-semibold text-[var(--ps-text-primary)] block">{community.name}</span>
            <span className="text-xs text-[var(--ps-text-muted)]">{community.member_count} members</span>
          </div>
        </div>
        {isOwner && (
          <span className="flex items-center gap-1 text-xs text-[var(--ps-gold)]">
            <Crown className="w-3 h-3" /> Owner
          </span>
        )}
      </div>
      {community.description && (
        <p className="text-sm text-[var(--ps-text-secondary)] mb-3 line-clamp-2">{community.description}</p>
      )}
      <div className="flex gap-2">
        {isOwner ? (
          <button
            onClick={() => setSelectedCommunity(community)}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-[var(--ps-bg-elevated)] text-[var(--ps-text-primary)] hover:bg-[var(--ps-bg-hover)]"
          >
            Manage <ChevronRight className="w-4 h-4" />
          </button>
        ) : isMember ? (
          <button
            onClick={() => handleLeaveCommunity(community.community_id)}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-red-500/10 text-red-500 hover:bg-red-500/20"
          >
            <LogOut className="w-4 h-4" /> Leave
          </button>
        ) : community.has_pending_request ? (
          <div className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-yellow-500/10 text-yellow-500">
            <Clock className="w-4 h-4" /> Pending
          </div>
        ) : (
          <button
            onClick={() => handleJoinCommunity(community.community_id)}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-[var(--ps-primary)] text-white hover:opacity-90"
          >
            <UserPlus className="w-4 h-4" /> Join
          </button>
        )}
      </div>
    </div>
  );

  // Handle accept request
  const handleAcceptRequest = async (requestId) => {
    try {
      await axios.post(`${API}/golf-buddies/request/${requestId}/accept`);
      toast.success('Golf buddy added!');
      fetchFriends();
      fetchRequests();
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  // Handle decline request
  const handleDeclineRequest = async (requestId) => {
    try {
      await axios.post(`${API}/golf-buddies/request/${requestId}/decline`);
      toast.success('Request declined');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to decline request');
    }
  };

  return (
    <div className="bg-[var(--ps-bg-base)] min-h-[calc(100vh-140px)]" data-testid="golf-buddies-page">
      {/* Welcome Header with Par Seeker branding */}
      <div className="p-4 border-b border-[var(--ps-border)]">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[var(--ps-primary)] font-bold text-sm">⛳ PAR SEEKER</span>
        </div>
        <h1 className="text-lg font-semibold text-[var(--ps-text-primary)]">Welcome to the Community</h1>
        <p className="text-xs text-[var(--ps-text-secondary)]">Connect with golfers, join communities, and share your golf journey</p>
      </div>

      {/* Quick Actions: Join Community & Create Community */}
      <div className="p-4 border-b border-[var(--ps-border)]">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setShowJoinCommunity(true)}
            className="flex items-center justify-center gap-2 p-4 rounded-xl bg-[var(--ps-primary)]/10 border border-[var(--ps-primary)]/30 hover:bg-[var(--ps-primary)]/20 transition-colors"
            data-testid="join-community-btn"
          >
            <Globe className="w-5 h-5 text-[var(--ps-primary)]" />
            <span className="font-medium text-[var(--ps-primary)]">Join Community</span>
          </button>
          <button
            onClick={() => setShowCreateCommunity(true)}
            className="flex items-center justify-center gap-2 p-4 rounded-xl bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] hover:bg-[var(--ps-bg-hover)] transition-colors"
            data-testid="create-community-btn"
          >
            <Plus className="w-5 h-5 text-[var(--ps-text-primary)]" />
            <span className="font-medium text-[var(--ps-text-primary)]">Create Community</span>
          </button>
        </div>
      </div>

      {/* Tabs - simplified */}
      <div className="flex border-b border-[var(--ps-border)] overflow-x-auto">
        {[
          { id: 'buddies', label: 'Golf Buddies', count: friends.length },
          { id: 'communities', label: 'Communities', count: ownedCommunities.length + joinedCommunities.length },
          { id: 'find', label: 'Find Golfers' },
          { id: 'requests', label: 'Requests', count: incomingRequests.length + communityRequests.length }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); clearSearch(); }}
            className={`flex-1 py-3 text-sm font-medium transition-colors relative whitespace-nowrap px-2 ${
              activeTab === tab.id 
                ? 'text-[var(--ps-primary)]' 
                : 'text-[var(--ps-text-secondary)]'
            }`}
            data-testid={`buddies-tab-${tab.id}`}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                tab.id === 'requests' && tab.count > 0 
                  ? 'bg-[var(--ps-primary)] text-white' 
                  : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)]'
              }`}>
                {tab.count}
              </span>
            )}
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--ps-primary)]" />
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Golf Buddies Tab */}
        {activeTab === 'buddies' && (
          <>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="dark-spinner" />
              </div>
            ) : friends.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-[var(--ps-text-muted)] mx-auto mb-3" />
                <p className="text-sm text-[var(--ps-text-secondary)] mb-4">
                  Your golf buddies will appear here
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => setActiveTab('find')}
                  className="border-[var(--ps-primary)] text-[var(--ps-primary)]"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Find Golfers
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {friends.map((friend) => (
                  <MemberCard key={friend.user_id} member={friend} showRemove />
                ))}
              </div>
            )}
          </>
        )}

        {/* Communities Tab */}
        {activeTab === 'communities' && (
          <div className="space-y-6">
            {/* Communities I Own */}
            {ownedCommunities.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[var(--ps-text-primary)] mb-3 uppercase tracking-wide flex items-center gap-2">
                  <Crown className="w-4 h-4 text-[var(--ps-gold)]" />
                  Communities You Created
                </h3>
                <div className="space-y-3">
                  {ownedCommunities.map((comm) => (
                    <CommunityCard key={comm.community_id} community={comm} isOwner />
                  ))}
                </div>
              </div>
            )}
            
            {/* Communities I've Joined */}
            {joinedCommunities.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[var(--ps-text-primary)] mb-3 uppercase tracking-wide flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Communities You've Joined
                </h3>
                <div className="space-y-3">
                  {joinedCommunities.map((comm) => (
                    <CommunityCard key={comm.community_id} community={comm} isMember />
                  ))}
                </div>
              </div>
            )}
            
            {/* Empty State */}
            {ownedCommunities.length === 0 && joinedCommunities.length === 0 && (
              <div className="text-center py-8">
                <Globe className="w-12 h-12 text-[var(--ps-text-muted)] mx-auto mb-3" />
                <p className="text-sm text-[var(--ps-text-secondary)] mb-4">
                  Join or create communities to connect with golfers
                </p>
                <div className="flex gap-2 justify-center">
                  <Button onClick={() => setShowJoinCommunity(true)} className="bg-[var(--ps-primary)]">
                    <Globe className="w-4 h-4 mr-2" />
                    Join
                  </Button>
                  <Button onClick={() => setShowCreateCommunity(true)} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Create
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <>
            {incomingRequests.length === 0 && outgoingRequests.length === 0 && communityRequests.length === 0 ? (
              <div className="text-center py-12">
                <Flag className="w-16 h-16 text-[var(--ps-text-muted)] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[var(--ps-text-primary)] mb-2">
                  No pending requests
                </h3>
                <p className="text-sm text-[var(--ps-text-secondary)]">
                  Golf buddy and community requests will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Community Join Requests (for owners) */}
                {communityRequests.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-[var(--ps-text-primary)] mb-3 uppercase tracking-wide flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Community Join Requests
                    </h3>
                    <div className="space-y-3">
                      {communityRequests.map((req) => (
                        <div 
                          key={req.request_id}
                          className="flex items-center justify-between p-4 bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)]"
                        >
                          <div>
                            <span className="font-semibold text-[var(--ps-text-primary)] block">{req.user_name}</span>
                            <span className="text-xs text-[var(--ps-text-muted)]">wants to join {req.community_name}</span>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleAcceptCommunityRequest(req.request_id)}
                              className="px-3 py-1.5 rounded-full text-sm font-medium bg-[var(--ps-primary)] text-white"
                            >
                              Accept
                            </button>
                            <button
                              onClick={() => handleDeclineCommunityRequest(req.request_id)}
                              className="px-3 py-1.5 rounded-full text-sm font-medium bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)]"
                            >
                              Decline
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Golf Buddy Requests */}
                {incomingRequests.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-[var(--ps-text-primary)] mb-3 uppercase tracking-wide">
                      Incoming Buddy Requests
                    </h3>
                    <div className="space-y-3">
                      {incomingRequests.map((req) => (
                        <RequestCard key={req.request_id} request={req} type="incoming" />
                      ))}
                    </div>
                  </div>
                )}
                
                {outgoingRequests.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold text-[var(--ps-text-primary)] mb-3 uppercase tracking-wide">
                      Sent Requests
                    </h3>
                    <div className="space-y-3">
                      {outgoingRequests.map((req) => (
                        <RequestCard key={req.request_id} request={req} type="outgoing" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Find Golfers Tab */}
        {activeTab === 'find' && (
          <>
            {/* Search Bar */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--ps-text-muted)]" />
              <Input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 w-full bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)] placeholder:text-[var(--ps-text-muted)]"
                data-testid="friend-search-input"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--ps-text-muted)] hover:text-[var(--ps-text-primary)]"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Search Results or All Members */}
            {isSearching && searchQuery ? (
              <>
                <h3 className="text-sm font-medium text-[var(--ps-text-secondary)] mb-3">
                  Search Results
                </h3>
                {searchResults.length === 0 ? (
                  <p className="text-center py-8 text-[var(--ps-text-muted)]">
                    No golfers found for "{searchQuery}"
                  </p>
                ) : (
                  <div className="space-y-3">
                    {searchResults.map((member) => (
                      <MemberCard key={member.user_id} member={member} />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <h3 className="text-sm font-medium text-[var(--ps-text-secondary)] mb-3">
                  All Members ({allMembers.length})
                </h3>
                {allMembers.length === 0 ? (
                  <p className="text-center py-8 text-[var(--ps-text-muted)]">
                    No members found
                  </p>
                ) : (
                  <div className="space-y-3">
                    {allMembers.map((member) => (
                      <MemberCard key={member.user_id} member={member} />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Invite Tab */}
        {activeTab === 'invite' && (
          <div className="space-y-6">
            {/* Invite Header */}
            <div className="text-center py-4">
              <div className="w-20 h-20 bg-[var(--ps-primary)]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserPlus className="w-10 h-10 text-[var(--ps-primary)]" />
              </div>
              <h2 className="text-xl font-bold text-[var(--ps-text-primary)] mb-2">
                Invite Friends to Par Seeker
              </h2>
              <p className="text-sm text-[var(--ps-text-secondary)]">
                Share the app with your golf buddies
              </p>
            </div>

            {/* Invite Options */}
            <div className="space-y-3">
              {/* Share Button (Native) */}
              <button
                onClick={shareNative}
                className="w-full flex items-center gap-4 p-4 bg-[var(--ps-primary)] text-white rounded-xl hover:opacity-90 transition-opacity"
                data-testid="share-native-btn"
              >
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Share2 className="w-6 h-6" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold">Share Invite</p>
                  <p className="text-sm text-white/80">Use your phone's share options</p>
                </div>
              </button>

              {/* Email Option */}
              <button
                onClick={shareViaEmail}
                className="w-full flex items-center gap-4 p-4 bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-xl hover:bg-[var(--ps-bg-hover)] transition-colors"
                data-testid="share-email-btn"
              >
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-500" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold text-[var(--ps-text-primary)]">Email Invite</p>
                  <p className="text-sm text-[var(--ps-text-secondary)]">Send via email</p>
                </div>
              </button>

              {/* SMS/Text Option */}
              <button
                onClick={shareViaSMS}
                className="w-full flex items-center gap-4 p-4 bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-xl hover:bg-[var(--ps-bg-hover)] transition-colors"
                data-testid="share-sms-btn"
              >
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-green-500" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold text-[var(--ps-text-primary)]">Text Message</p>
                  <p className="text-sm text-[var(--ps-text-secondary)]">Send via SMS</p>
                </div>
              </button>

              {/* Copy Link Option */}
              <button
                onClick={copyInviteLink}
                className="w-full flex items-center gap-4 p-4 bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-xl hover:bg-[var(--ps-bg-hover)] transition-colors"
                data-testid="copy-link-btn"
              >
                <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                  {copied ? (
                    <Check className="w-6 h-6 text-green-500" />
                  ) : (
                    <Copy className="w-6 h-6 text-purple-500" />
                  )}
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold text-[var(--ps-text-primary)]">
                    {copied ? 'Link Copied!' : 'Copy Invite Link'}
                  </p>
                  <p className="text-sm text-[var(--ps-text-secondary)] truncate">
                    {inviteLink}
                  </p>
                </div>
              </button>
            </div>

            {/* Invite Preview */}
            <div className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-xl p-4">
              <p className="text-xs text-[var(--ps-text-muted)] uppercase mb-2">Preview Message</p>
              <p className="text-sm text-[var(--ps-text-secondary)]">
                {inviteMessage}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* User Profile Modal */}
      {selectedUserId && (
        <UserProfileModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          currentUser={user}
          onMessageSent={() => {
            toast.success('Check your Chat to continue the conversation');
          }}
        />
      )}

      {/* Create Community Modal */}
      {showCreateCommunity && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--ps-bg-surface)] rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-[var(--ps-border)] flex items-center justify-between">
              <h2 className="text-lg font-bold text-[var(--ps-text-primary)]">Create Community</h2>
              <button onClick={() => setShowCreateCommunity(false)} className="p-2 hover:bg-[var(--ps-bg-hover)] rounded-full">
                <X className="w-5 h-5 text-[var(--ps-text-secondary)]" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--ps-text-secondary)] mb-2">Community Name</label>
                <Input
                  value={newCommunity.name}
                  onChange={(e) => setNewCommunity(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Denver Golf Club"
                  className="bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--ps-text-secondary)] mb-2">Description</label>
                <textarea
                  value={newCommunity.description}
                  onChange={(e) => setNewCommunity(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What's your community about?"
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] text-[var(--ps-text-primary)] placeholder:text-[var(--ps-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--ps-primary)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--ps-text-secondary)] mb-2">Privacy</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setNewCommunity(prev => ({ ...prev, privacy: 'public' }))}
                    className={`p-3 rounded-lg border flex items-center gap-2 ${newCommunity.privacy === 'public' ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10' : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)]'}`}
                  >
                    <Globe className={`w-5 h-5 ${newCommunity.privacy === 'public' ? 'text-[var(--ps-primary)]' : 'text-[var(--ps-text-secondary)]'}`} />
                    <span className={`text-sm font-medium ${newCommunity.privacy === 'public' ? 'text-[var(--ps-primary)]' : 'text-[var(--ps-text-secondary)]'}`}>Public</span>
                  </button>
                  <button
                    onClick={() => setNewCommunity(prev => ({ ...prev, privacy: 'private' }))}
                    className={`p-3 rounded-lg border flex items-center gap-2 ${newCommunity.privacy === 'private' ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10' : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)]'}`}
                  >
                    <Lock className={`w-5 h-5 ${newCommunity.privacy === 'private' ? 'text-[var(--ps-primary)]' : 'text-[var(--ps-text-secondary)]'}`} />
                    <span className={`text-sm font-medium ${newCommunity.privacy === 'private' ? 'text-[var(--ps-primary)]' : 'text-[var(--ps-text-secondary)]'}`}>Private</span>
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--ps-text-secondary)] mb-2">Tags (comma separated)</label>
                <Input
                  value={newCommunity.tags}
                  onChange={(e) => setNewCommunity(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="e.g., beginners, weekend golfers, tournaments"
                  className="bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                />
              </div>
              <Button onClick={handleCreateCommunity} className="w-full bg-[var(--ps-primary)]">
                Create Community
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Join Community Modal */}
      {showJoinCommunity && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--ps-bg-surface)] rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-[var(--ps-border)] flex items-center justify-between">
              <h2 className="text-lg font-bold text-[var(--ps-text-primary)]">Find Communities</h2>
              <button onClick={() => { setShowJoinCommunity(false); setCommunitySearch(''); setCommunitySearchResults([]); }} className="p-2 hover:bg-[var(--ps-bg-hover)] rounded-full">
                <X className="w-5 h-5 text-[var(--ps-text-secondary)]" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex gap-2">
                <Input
                  value={communitySearch}
                  onChange={(e) => setCommunitySearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchCommunities()}
                  placeholder="Search communities..."
                  className="flex-1 bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                />
                <Button onClick={handleSearchCommunities} className="bg-[var(--ps-primary)]">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              
              {communitySearchResults.length > 0 ? (
                <div className="space-y-3">
                  {communitySearchResults.map((comm) => (
                    <CommunityCard key={comm.community_id} community={comm} isMember={comm.is_member} />
                  ))}
                </div>
              ) : communitySearch ? (
                <p className="text-center text-sm text-[var(--ps-text-muted)] py-8">No communities found</p>
              ) : (
                <>
                  <p className="text-sm text-[var(--ps-text-muted)]">Popular Communities</p>
                  <div className="space-y-3">
                    {communities.slice(0, 5).map((comm) => (
                      <CommunityCard key={comm.community_id} community={comm} isMember={comm.is_member} />
                    ))}
                    {communities.length === 0 && (
                      <p className="text-center text-sm text-[var(--ps-text-muted)] py-4">No communities yet. Be the first to create one!</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Buddy Request Review Modal - shown when clicking notification */}
      {reviewingRequest && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          data-testid="buddy-request-review-modal"
        >
          <div className="bg-[var(--ps-bg-surface)] rounded-2xl w-full max-w-sm overflow-hidden animate-fade-in">
            {/* Header */}
            <div className="p-4 border-b border-[var(--ps-border)] flex items-center justify-between">
              <h2 className="text-lg font-bold text-[var(--ps-text-primary)]">Golf Buddy Request</h2>
              <button 
                onClick={() => setReviewingRequest(null)} 
                className="p-2 hover:bg-[var(--ps-bg-hover)] rounded-full"
                data-testid="close-request-review"
              >
                <X className="w-5 h-5 text-[var(--ps-text-secondary)]" />
              </button>
            </div>
            
            {/* Requester Info */}
            <div className="p-6 text-center">
              <div className="flex justify-center mb-4">
                <UserBubble
                  userId={reviewingRequest.from_user_id}
                  picture={reviewingRequest.from_user_picture}
                  name={reviewingRequest.from_user_name}
                  bubbleColor={reviewingRequest.bubble_color || 'green'}
                  isOnline={reviewingRequest.is_online}
                  size="xl"
                  onClick={() => {
                    setReviewingRequest(null);
                    setSelectedUserId(reviewingRequest.from_user_id);
                  }}
                />
              </div>
              <h3 className="text-xl font-bold text-[var(--ps-text-primary)] mb-1">
                {reviewingRequest.from_user_name}
              </h3>
              <p className="text-sm text-[var(--ps-text-secondary)] mb-2">
                wants to be your golf buddy
              </p>
              {reviewingRequest.from_user_handicap !== null && reviewingRequest.from_user_handicap !== undefined && (
                <p className="text-xs text-[var(--ps-text-muted)]">
                  Handicap: {reviewingRequest.from_user_handicap}
                </p>
              )}
              <p className="text-xs text-[var(--ps-text-muted)] mt-2">
                <Clock className="w-3 h-3 inline mr-1" />
                {reviewingRequest.created_at ? new Date(reviewingRequest.created_at).toLocaleDateString() : 'Recently'}
              </p>
            </div>
            
            {/* Actions */}
            <div className="p-4 space-y-3 border-t border-[var(--ps-border)]">
              {/* Accept Button */}
              <Button 
                onClick={async () => {
                  await handleAcceptRequest(reviewingRequest.request_id);
                  setReviewingRequest(null);
                }}
                className="w-full bg-[var(--ps-primary)] hover:opacity-90"
                data-testid="accept-buddy-request-btn"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Accept Request
              </Button>
              
              {/* Message Button - Opens chat */}
              <Button 
                variant="outline" 
                onClick={() => {
                  // Navigate to chat with this user
                  navigate(`/chat?start_chat=${reviewingRequest.from_user_id}`);
                  setReviewingRequest(null);
                }}
                className="w-full border-[var(--ps-border)] text-[var(--ps-text-primary)] hover:bg-[var(--ps-bg-hover)]"
                data-testid="message-requester-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Message First
              </Button>
              
              {/* Decline/Ignore */}
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  onClick={() => setReviewingRequest(null)}
                  className="flex-1 text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]"
                  data-testid="ignore-buddy-request-btn"
                >
                  Ignore
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={async () => {
                    await handleDeclineRequest(reviewingRequest.request_id);
                    setReviewingRequest(null);
                  }}
                  className="flex-1 text-red-500 hover:bg-red-500/10"
                  data-testid="delete-buddy-request-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FriendsPage;
